import React, { useMemo } from 'react';
import { Select, InputNumber, Collapse, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { POPULAR_MAKES } from '../mysearchConstants';
import { DEFAULT_CAR_IMAGE } from '../../components/SelectWithImages';

const currentYear = new Date().getFullYear();
const buildYearOptions = (anyLabel: string) => [
  { value: '', label: anyLabel },
  ...Array.from({ length: currentYear - 1980 + 1 }, (_, i) => {
    const y = String(currentYear - i);
    return { value: y, label: y };
  }),
];
import { MdTune, MdFilterListOff } from 'react-icons/md';
import {
  FilterState,
  conditionOptions,
  bodyTypeOptions,
  fuelTypeOptions,
  sellerTypeOptions,
  colors,
} from '../mysearchConstants';
import { IMPORT_COUNTRY_OPTIONS } from '../../AddUserListing/constants/listingOptions';

interface FilterSidebarProps {
  filters: FilterState;
  makeOptions: { value: string; label: string; image?: string }[];
  modelOptions: { value: string; label: string }[];
  loadingModels: boolean;
  activeFilterCount: number;
  selectedColor: string;
  isMetallic: boolean;
  onFilterChange: (key: keyof FilterState, value: any) => void;
  onMakeChange: (value: string) => void;
  onColorChange: (color: string) => void;
  onMetallicChange: (val: boolean) => void;
  onReset: () => void;
  onClose?: () => void;
}

const colorNames: Record<string, string> = {
  '#1d1d1d': 'Black', '#ffffff': 'White', '#b4b4b4': 'Silver',
  '#808080': 'Gray', '#d7b48c': 'Beige', '#8a5b24': 'Brown',
  '#ff3b30': 'Red', '#ff7b00': 'Orange', '#ffcc00': 'Yellow',
  '#3366ff': 'Blue', '#6cb33f': 'Green', '#d4c542': 'Gold',
  '#9f77ff': 'Purple', 'linear-gradient(135deg,#eee,#bbb,#eee)': 'Chrome',
};

const buildGroupedMakeOptions = (opts: { value: string; label: string; image?: string }[]) => {
  const popularOpts = opts.filter(o =>
    POPULAR_MAKES.some(pm => pm.toLowerCase() === o.label.toLowerCase())
  );
  if (!popularOpts.length) return opts;
  return [
    { label: 'Popular Makes', options: popularOpts },
    { label: 'All Makes', options: opts },
  ];
};

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters, makeOptions, modelOptions, loadingModels,
  activeFilterCount, selectedColor, isMetallic,
  onFilterChange, onMakeChange, onColorChange, onMetallicChange, onReset, onClose,
}) => {
  const { t } = useTranslation();
  const groupedMakeOptions = useMemo(() => buildGroupedMakeOptions(makeOptions), [makeOptions]);
  const yearOptions = useMemo(() => buildYearOptions(t('filters.any')), [t]);
  const translateOptionLabel = (label: string) => {
    const map: Record<string, string> = {
      'Any': t('filters.any'),
      'New': t('image_slider.filter_new'),
      'Used': t('image_slider.filter_used'),
      'Dealer': t('common.dealer'),
      'Private Seller': t('common.private'),
    };
    return map[label] || label;
  };
  return (
  <aside className="fs-root">
    {/* Header */}
    <div className="fs-header">
      <div className="fs-header-left">
        <MdTune size={18} style={{ color: '#fe5a1d' }} />
        <span className="fs-title">{t('filters.title')}</span>
        {activeFilterCount > 0 && <span className="fs-count">{activeFilterCount}</span>}
      </div>
      <div className="fs-header-right">
        {activeFilterCount > 0 && (
          <button className="fs-clear" onClick={onReset}>
            <MdFilterListOff size={14} /> {t('filters.clear_all')}
          </button>
        )}
        {onClose && (
          <button className="fs-close-btn d-md-none" onClick={onClose} aria-label={t('filters.close')}>
            ✕
          </button>
        )}
      </div>
    </div>

    {/* Imported Vehicles */}
    <div className="fs-section">
      <span className="fs-section-label">{t('filters.imported_section')}</span>
      <Checkbox
        checked={!!filters.isImported}
        onChange={(e) => onFilterChange('isImported', e.target.checked)}
        style={{ marginTop: 6, fontSize: 13 }}
      >
        {t('filters.imported_only')}
      </Checkbox>
      {filters.isImported && (
        <>
          <Select
            className="fs-select"
            placeholder={t('search.any_country')}
            value={filters.importCountry || undefined}
            onChange={(val) => onFilterChange('importCountry', val ?? '')}
            allowClear
            onClear={() => onFilterChange('importCountry', '')}
            options={IMPORT_COUNTRY_OPTIONS}
            showSearch
            popupClassName="fs-dropdown"
            style={{ width: '100%', marginTop: 8 }}
          />
          {/* Row 1: Imported only + All */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              className={`fs-pill ${filters.importAvailability === '' ? 'fs-pill-active' : ''}`}
              onClick={() => onFilterChange('importAvailability', '')}
              type="button"
            >
              {t('filters.all')}
            </button>
          </div>
          {/* Row 2: Available Now + Ready to Import */}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            {[
              { value: 'AVAILABLE_NOW', label: t('filters.available_now') },
              { value: 'READY_TO_IMPORT', label: t('filters.ready_to_import') },
            ].map((opt) => (
              <button
                key={opt.value}
                className={`fs-pill ${filters.importAvailability === opt.value ? 'fs-pill-active' : ''}`}
                onClick={() => onFilterChange('importAvailability', opt.value)}
                type="button"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>

    {/* Condition */}
    <div className="fs-section">
      <span className="fs-section-label">{t('filters.condition')}</span>
      <div className="fs-pill-group">
        {conditionOptions.map(opt => (
          <button
            key={opt.value || 'any'}
            className={`fs-pill ${filters.condition === opt.value ? 'fs-pill-active' : ''}`}
            onClick={() => onFilterChange('condition', opt.value)}
          >
            {translateOptionLabel(opt.label)}
          </button>
        ))}
      </div>
    </div>

    {/* Make & Model */}
    <div className="fs-section">
      <span className="fs-section-label">{t('filters.make_model')}</span>
      <Select
        className="fs-select"
        placeholder={t('search.any_make')}
        value={filters.make || undefined}
        onChange={val => onMakeChange(val ?? '')}
        showSearch
        allowClear
        onClear={() => onMakeChange('')}
        options={groupedMakeOptions as any}
        popupClassName="fs-dropdown"
        style={{ width: '100%', marginBottom: 8 }}
        filterOption={(input, opt) =>
          String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        optionRender={option => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img
              src={(option.data as any).image || DEFAULT_CAR_IMAGE}
              alt={String(option.label)}
              style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0, opacity: (option.data as any).image ? 1 : 0.45 }}
              onError={e => { e.currentTarget.src = DEFAULT_CAR_IMAGE; e.currentTarget.style.opacity = '0.45'; }}
            />
            <span>{option.label}</span>
          </div>
        )}
      />
      {filters.make && (
        <Select
          className="fs-select"
          placeholder={t('search.any_model')}
          value={filters.model || undefined}
          onChange={val => onFilterChange('model', val ?? '')}
          showSearch
          allowClear
          onClear={() => onFilterChange('model', '')}
          disabled={loadingModels}
          loading={loadingModels}
          options={modelOptions}
          popupClassName="fs-dropdown"
          style={{ width: '100%' }}
          filterOption={(input, opt) =>
            (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      )}
    </div>

    {/* Body Type */}
    <div className="fs-section">
      <span className="fs-section-label">{t('filters.body_type')}</span>
      <Select
        className="fs-select"
        placeholder={t('search.any_body_type')}
        value={filters.bodyType || undefined}
        onChange={val => onFilterChange('bodyType', val ?? '')}
        allowClear
        onClear={() => onFilterChange('bodyType', '')}
        options={bodyTypeOptions.filter(o => o.value).map(o => ({ value: o.value, label: o.label }))}
        popupClassName="fs-dropdown"
        style={{ width: '100%' }}
      />
    </div>

    {/* Price */}
    <div className="fs-section">
      <span className="fs-section-label">{t('filters.price_label')}</span>
      <div className="fs-range">
        <InputNumber
          placeholder={t('search.min')}
          value={filters.priceFrom ? Number(filters.priceFrom) : undefined}
          onChange={val => onFilterChange('priceFrom', val ?? '')}
          style={{ width: '100%' }}
          formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          min={0}
        />
        <span className="fs-range-sep">—</span>
        <InputNumber
          placeholder={t('search.max')}
          value={filters.priceTo ? Number(filters.priceTo) : undefined}
          onChange={val => onFilterChange('priceTo', val ?? '')}
          style={{ width: '100%' }}
          formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          min={0}
        />
      </div>
    </div>

    {/* Seller Type */}
    <div className="fs-section">
      <span className="fs-section-label">{t('filters.seller')}</span>
      <div className="fs-pill-group">
        {sellerTypeOptions.filter(opt => opt.value !== 'PRIVATE').map(opt => (
          <button
            key={opt.value || 'any'}
            className={`fs-pill ${filters.sellerType === opt.value ? 'fs-pill-active' : ''}`}
            onClick={() => onFilterChange('sellerType', opt.value)}
          >
            {translateOptionLabel(opt.label)}
          </button>
        ))}
        <div style={{ flexBasis: '100%', height: 0 }} />
        {sellerTypeOptions.filter(opt => opt.value === 'PRIVATE').map(opt => (
          <button
            key={opt.value}
            className={`fs-pill ${filters.sellerType === opt.value ? 'fs-pill-active' : ''}`}
            onClick={() => onFilterChange('sellerType', opt.value)}
          >
            {translateOptionLabel(opt.label)}
          </button>
        ))}
      </div>
    </div>

    {/* Price Tag */}
    <div className="fs-section">
      <span className="fs-section-label">{t('filters.price_rating')}</span>
      <div className="fs-pill-group">
        {[{ label: t('filters.any'), value: '' }, { label: t('filters.good_price'), value: 'GOOD_PRICE' }].map(opt => (
          <button
            key={opt.value || 'any'}
            className={`fs-pill ${filters.priceTag === opt.value ? 'fs-pill-active' : ''}`}
            onClick={() => onFilterChange('priceTag', opt.value)}
          >
            {opt.label}
          </button>
        ))}
        <div style={{ flexBasis: '100%', height: 0 }} />
        {[{ label: t('filters.fair_price'), value: 'FAIR_PRICE' }, { label: t('filters.high_price'), value: 'HIGH_PRICE' }].map(opt => (
          <button
            key={opt.value}
            className={`fs-pill ${filters.priceTag === opt.value ? 'fs-pill-active' : ''}`}
            onClick={() => onFilterChange('priceTag', opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>

    {/* Advanced (accordion) */}
    <Collapse ghost expandIconPosition="end" defaultActiveKey={['1', '2']} style={{ marginTop: 4 }}>
      <Collapse.Panel header={<span className="fs-section-label" style={{ marginBottom: 0 }}>{t('filters.year_mileage')}</span>} key="1">
        <div className="fs-section" style={{ paddingTop: 8 }}>
          <span className="fs-sub-label">{t('filters.year')}</span>
          <div className="fs-range">
            <Select
              className="fs-select"
              placeholder={t('filters.from')}
              value={filters.yearFrom || undefined}
              onChange={val => onFilterChange('yearFrom', val ?? '')}
              allowClear
              onClear={() => onFilterChange('yearFrom', '')}
              options={yearOptions}
              popupClassName="fs-dropdown"
              style={{ width: '100%' }}
            />
            <span className="fs-range-sep">—</span>
            <Select
              className="fs-select"
              placeholder={t('filters.to')}
              value={filters.yearTo || undefined}
              onChange={val => onFilterChange('yearTo', val ?? '')}
              allowClear
              onClear={() => onFilterChange('yearTo', '')}
              options={yearOptions}
              popupClassName="fs-dropdown"
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div className="fs-section" style={{ paddingTop: 8 }}>
          <span className="fs-sub-label">{t('filters.mileage_km')}</span>
          <div className="fs-range">
            <InputNumber placeholder={t('search.min')} value={filters.mileageFrom ? Number(filters.mileageFrom) : undefined}
              onChange={val => onFilterChange('mileageFrom', val ?? '')} style={{ width: '100%' }} min={0} />
            <span className="fs-range-sep">—</span>
            <InputNumber placeholder={t('search.max')} value={filters.mileageTo ? Number(filters.mileageTo) : undefined}
              onChange={val => onFilterChange('mileageTo', val ?? '')} style={{ width: '100%' }} min={0} />
          </div>
        </div>
      </Collapse.Panel>

      <Collapse.Panel header={<span className="fs-section-label" style={{ marginBottom: 0 }}>{t('filters.engine_colour')}</span>} key="2">
        <div className="fs-section" style={{ paddingTop: 8 }}>
          <span className="fs-sub-label">{t('filters.fuel_type')}</span>
          <Select
            placeholder={t('search.any')}
            value={filters.fuelType || undefined}
            onChange={val => onFilterChange('fuelType', val ?? '')}
            allowClear
            onClear={() => onFilterChange('fuelType', '')}
            options={fuelTypeOptions.filter(o => o.value).map(o => ({ value: o.value, label: o.label }))}
            popupClassName="fs-dropdown"
            style={{ width: '100%', marginTop: 6 }}
          />
        </div>
        <div className="fs-section" style={{ paddingTop: 8 }}>
          <span className="fs-sub-label">{t('filters.exterior_colour')}</span>
          <div className="fs-color-grid">
            {colors.map(color => (
              <div
                key={color}
                title={colorNames[color] || color}
                className={`fs-color-dot ${colorNames[color] === selectedColor ? 'fs-color-active' : ''}`}
                style={{ background: color, border: color === '#ffffff' ? '1px solid #ccc' : undefined }}
                onClick={() => onColorChange(colorNames[color] === selectedColor ? '' : colorNames[color] || color)}
              />
            ))}
          </div>
          <div
            className={`fs-color-other${selectedColor === 'Other' ? ' fs-color-other-active' : ''}`}
            onClick={() => onColorChange(selectedColor === 'Other' ? '' : 'Other')}
          >
            {t('filters.other')}
          </div>
          <Checkbox
            checked={isMetallic}
            onChange={e => onMetallicChange(e.target.checked)}
            style={{ marginTop: 10, fontSize: 13 }}
          >
            {t('filters.metallic')}
          </Checkbox>
        </div>
      </Collapse.Panel>
    </Collapse>
  </aside>
  );
};

export default FilterSidebar;
